import React, { useEffect, useState } from "react";

import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Paper,
  Stack,
  TextField,
  Typography,
  Divider,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import { Autocomplete, FormControl } from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";

import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const UserCollegeAssignment = () => {
  const user = JSON.parse(sessionStorage.getItem("user") || "{}");

  const clientId = user.client_id;
  const currentCollegeId = user.college_id;

  const [users, setUsers] = useState([]);
  const [colleges, setColleges] = useState([]);

  const [selectedUserId, setSelectedUserId] = useState("");

  const [selectedCollegeIds, setSelectedCollegeIds] = useState([]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [search, setSearch] = useState("");

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const closeSnackbar = () => {
    setSnackbar((prev) => ({
      ...prev,
      open: false,
    }));
  };

  // ---------------------------------------------------------
  // LOAD USERS
  // ---------------------------------------------------------

  const loadUsers = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/rbacall/users/${clientId}/${currentCollegeId}`,
        {
          params: {
            search,
          },
        }
      );

      setUsers(response.data?.data || response.data || []);
    } catch (error) {
      console.error(error);

      showSnackbar(error.response?.data?.message || "Failed to load users", "error");
    }
  };

  // ---------------------------------------------------------
  // LOAD COLLEGES
  // ---------------------------------------------------------

  const loadColleges = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/rbacall/colleges/${clientId}`);

      setColleges(response.data?.data || response.data || []);
    } catch (error) {
      console.error(error);

      showSnackbar(error.response?.data?.message || "Failed to load colleges", "error");
    }
  };

  // ---------------------------------------------------------
  // LOAD ASSIGNED COLLEGES
  // ---------------------------------------------------------

  const loadAssignedColleges = async (userId) => {
    if (!userId) {
      setSelectedCollegeIds([]);
      return;
    }

    try {
      setLoading(true);

      const response = await axios.get(
        `${API_BASE_URL}/api/rbacall/user-colleges/${clientId}/${userId}`
      );

      const data = response.data?.data || response.data || [];

      setSelectedCollegeIds(data.map((item) => Number(item.college_id ?? item)));
    } catch (error) {
      console.error(error);

      showSnackbar(error.response?.data?.message || "Failed to load user colleges", "error");

      setSelectedCollegeIds([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (clientId && currentCollegeId) {
      loadUsers();
      loadColleges();
    }
  }, [clientId, currentCollegeId]);

  useEffect(() => {
    if (clientId && currentCollegeId) {
      loadUsers();
    }
  }, [search]);

  // ---------------------------------------------------------
  // USER CHANGE
  // ---------------------------------------------------------

  const handleUserChange = async (userId) => {
    setSelectedUserId(userId);

    await loadAssignedColleges(userId);
  };

  // ---------------------------------------------------------
  // COLLEGE CHECKBOX
  // ---------------------------------------------------------

  const handleCollegeChange = async (collegeId, checked) => {
    const id = Number(collegeId);

    /*
     * Selecting is always allowed
     */
    if (checked) {
      setSelectedCollegeIds((previous) => {
        if (previous.includes(id)) {
          return previous;
        }

        return [...previous, id];
      });

      return;
    }

    /*
     * Before de-allocating,
     * verify role assignments.
     */
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/rbacall/user-college-roles-exists/${selectedUserId}/${id}`
      );

      if (response.data?.exists) {
        showSnackbar(
          "Roles are already assigned for this college. Remove role assignments first.",
          "error"
        );

        return;
      }

      setSelectedCollegeIds((previous) => previous.filter((item) => Number(item) !== id));
    } catch (error) {
      console.error(error);

      showSnackbar("Unable to validate role assignments", "error");
    }
  };

  // ---------------------------------------------------------
  // SELECT ALL
  // ---------------------------------------------------------

  const allSelected =
    colleges.length > 0 &&
    colleges.every((college) => selectedCollegeIds.includes(Number(college.college_id)));

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedCollegeIds(colleges.map((college) => Number(college.college_id)));
    } else {
      setSelectedCollegeIds([]);
    }
  };

  // ---------------------------------------------------------
  // SAVE
  // ---------------------------------------------------------

  const handleSave = async () => {
    if (!selectedUserId) {
      showSnackbar("Please select user", "error");
      return;
    }

    try {
      setSaving(true);

      await axios.put(`${API_BASE_URL}/api/rbacall/user-colleges/${clientId}/${selectedUserId}`, {
        user_id: Number(selectedUserId),
        college_ids: selectedCollegeIds,
        client_id: clientId,
        modified_by: user.user_id || null,
      });

      showSnackbar("User college assignments saved successfully");

      loadAssignedColleges(selectedUserId);
    } catch (error) {
      console.error(error);

      showSnackbar(error.response?.data?.message || "Failed to save college assignments", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box p={2}>
      <Paper elevation={2}>
        <Box p={2}>
          <Typography variant="h5" mb={2}>
            ASSIGN COLLEGES TO USER
          </Typography>

          <Stack
            spacing={2}
            sx={{
              width: "100%",
              maxWidth: 500,
            }}
          >
            {/* Search User */}
            <TextField
              size="medium"
              label="Search User"
              placeholder="Name / Email / Mobile"
              fullWidth
              sx={{
                width: {
                  xs: "100%",
                  sm: 350,
                  md: 360,
                },
              }}
              InputLabelProps={{
                shrink: true,
              }}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            {/* User Selection */}

            <Autocomplete
              size="medium"
              fullWidth
              options={users}
              getOptionLabel={(option) =>
                option
                  ? `${option.user_name || ""} — ${option.user_email || ""} — ${
                      option.user_mobile || ""
                    }`
                  : ""
              }
              value={users.find((item) => Number(item.user_id) === Number(selectedUserId)) || null}
              onChange={(event, newValue) => {
                handleUserChange(newValue?.user_id || "");
              }}
              isOptionEqualToValue={(option, value) =>
                Number(option.user_id) === Number(value.user_id)
              }
              renderOption={(props, option) => (
                <li {...props} key={option.user_id}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      width: "100%",
                    }}
                  >
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      sx={{
                        fontSize: {
                          xs: "0.9rem",
                          sm: "0.975rem",
                        },
                      }}
                      color="black"
                    >
                      {option.user_name || "Unknown User"}
                    </Typography>

                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        wordBreak: "break-word",
                        fontSize: {
                          xs: "0.8rem",
                          sm: "0.875rem",
                        },
                      }}
                    >
                      {[option.user_email, option.user_mobile].filter(Boolean).join(" • ")}
                    </Typography>
                  </Box>
                </li>
              )}
              renderInput={(paramsInput) => (
                <TextField
                  {...paramsInput}
                  size="medium"
                  label="Select User"
                  placeholder="Search user..."
                  InputLabelProps={{
                    shrink: true,
                  }}
                  required
                  onKeyDown={(e) => {
                    e.stopPropagation();
                  }}
                />
              )}
            />
          </Stack>
        </Box>

        {selectedUserId && (
          <Box p={2}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
              <Typography variant="subtitle1">ASSIGNED COLLEGES</Typography>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={allSelected}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                }
                label="Select All"
              />
            </Stack>

            {loading ? (
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
              </Box>
            ) : (
              <FormGroup>
                {colleges.map((college) => {
                  const id = Number(college.college_id);

                  return (
                    <FormControlLabel
                      key={id}
                      control={
                        <Checkbox
                          checked={selectedCollegeIds.includes(id)}
                          onChange={(e) => handleCollegeChange(id, e.target.checked)}
                        />
                      }
                      label={college.college_name || college.name}
                    />
                  );
                })}
              </FormGroup>
            )}

            <Box mt={3}>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                disabled={saving || loading}
                onClick={handleSave}
                sx={{ color: "#fff" }}
              >
                {saving ? "Saving..." : "Assign Colleges"}
              </Button>
            </Box>
          </Box>
        )}

        {!selectedUserId && (
          <Box p={4}>
            <Typography align="center" color="text.secondary">
              Select a user to manage college assignments.
            </Typography>
          </Box>
        )}
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={closeSnackbar}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Alert onClose={closeSnackbar} severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserCollegeAssignment;
